import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  MenuItem,
  Autocomplete,
  Chip,
  Select,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List as MUIList,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Add as AddIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { surveyAPI, userAPI, teamAPI, organizationAPI } from '../services/api';
import RichTextEditor from '../components/RichTextEditor';
import SurveyQuestionCard from '../components/survey/QuestionCard';
import { Question, User, Team, Organization, Section } from '../types';
import { useAuth } from '../context/AuthContext';

const CreateSurvey: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!id;
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isBasicManager = user?.role === 'manager' && (user as any).accountType === 'basic';
  const isStandardManager = user?.role === 'manager' && (user as any).accountType === 'standard';
  const canUseTemplates = user && (user.role === 'admin' || user.role === 'manager');
  
  // Check if using a template (Basic managers can only use templates, not create blank surveys)
  const params = new URLSearchParams(location.search);
  const templateId = params.get('templateId');
  const isUsingTemplate = !!templateId;
  
  // Read-only questions: Basic managers always have read-only questions (can see but not edit)
  const isReadOnlyQuestions = isBasicManager;
  

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    introHtml: '',
    // Admins creating a new survey can omit dates; default to empty and inactive
    startDate: (isAdmin && !isEditMode) ? '' : new Date().toISOString().split('T')[0],
    endDate: (isAdmin && !isEditMode) ? '' : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    targetDepartment: '',
    anonymousResponses: false,
    isActive: (isAdmin && !isEditMode) ? false : true,
    isTemplate: false,
  });

  const [questions, setQuestions] = useState<Question[]>([
    {
      questionText: '',
      questionType: 'text',
      options: [],
      ratingMinLabel: '',
      ratingMaxLabel: '',
      ratingScale: 5,
      ratingLabels: ['', '', '', '', ''],
      required: false,
      order: 1,
    },
  ]);

  // Template dialog state
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');

  // Assignment state
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [allOrgs, setAllOrgs] = useState<Organization[]>([]);
  // Admin visibility for templates
  const [adminVisibility, setAdminVisibility] = useState<'global' | 'selected' | 'org'>('global');
  const [adminSelectedOrgs, setAdminSelectedOrgs] = useState<Organization[]>([]);
  
  // Sections
  const [sections, setSections] = useState<Section[]>([]);
  // Local UI state: boundaries specifying where a section starts in the questions list
  const [sectionBoundaries, setSectionBoundaries] = useState<{ sectionId: string; index: number }[]>([]);
  const [assignedUsersSel, setAssignedUsersSel] = useState<User[]>([]);
  const [assignedTeamsSel, setAssignedTeamsSel] = useState<Team[]>([]);
  const [initialAssignedUserIds, setInitialAssignedUserIds] = useState<string[]>([]);
  const [initialAssignedTeamIds, setInitialAssignedTeamIds] = useState<string[]>([]);
  // Pagination for questions
  const [page, setPage] = useState(0);
  const pageSize = 10;
  // No parent-level draft; each QuestionCard holds its own draft text

  // Redirect Basic managers away from create screen (unless they're using a template)
  useEffect(() => {
    if (isBasicManager && !isEditMode && !isUsingTemplate) {
      navigate('/dashboard');
    }
  }, [isBasicManager, isEditMode, isUsingTemplate, navigate]);

  // Clear any error messages for Basic managers using templates
  useEffect(() => {
    if (isBasicManager && isUsingTemplate) {
      setError('');
    }
  }, [isBasicManager, isUsingTemplate]);

  useEffect(() => {
    // Load options for assignments
    const load = async () => {
      try {
        const promises: any[] = [userAPI.getUsers(), teamAPI.getTeams()];
        if (isAdmin && !isEditMode) {
          promises.push(organizationAPI.getOrganizations());
        }
        const [usersRes, teamsRes, orgsRes] = await Promise.all(promises);
        setAllUsers(usersRes.data || []);
        setAllTeams(teamsRes.data || []);
        if (orgsRes) setAllOrgs(orgsRes.data || []);
      } catch (e) {
        // non-blocking
      }
    };
    load();
    if (isEditMode && id) fetchSurvey();
    // If creating and a templateId is present, prefill from template without creating a survey
    if (!isEditMode) {
      const params = new URLSearchParams(location.search);
      const templateId = params.get('templateId');
      if (templateId) {
        (async () => {
          try {
            setLoading(true);
            const res = await surveyAPI.getSurvey(templateId);
            const tpl = res.data;
            // Prefill fields (do not mark as template, do not set dates)
            setFormData((prev) => ({
              ...prev,
              title: tpl.title || prev.title,
              description: tpl.description || '',
              introHtml: tpl.introHtml || '',
              // Keep existing date defaults for managers; admin template creation handled in submit
              startDate: prev.startDate,
              endDate: prev.endDate,
              targetDepartment: tpl.targetDepartment || '',
              anonymousResponses: !!tpl.anonymousResponses,
              isActive: prev.isActive,
              isTemplate: false,
            }));
            // Clone questions, clear _id, reassign order, normalize allowComment default
            const clonedQs = (tpl.questions || []).map((q: any, idx: number) => ({
              ...q,
              _id: undefined,
              order: idx + 1,
              allowComment: typeof q.allowComment === 'boolean'
                ? q.allowComment
                : (q.questionType === 'rating' || q.questionType === 'multiple_choice'),
            }));
            setQuestions(clonedQs);
            // Clear any initial assignments
            setInitialAssignedUserIds([]);
            setInitialAssignedTeamIds([]);
          } catch (e: any) {
            setError(e.response?.data?.message || 'Failed to load template');
          } finally {
            setLoading(false);
          }
        })();
      }
    }
  }, [id, isEditMode, location.search]);

  // Sections helpers (top-level scope)
  const newSectionId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const addSection = () => {
    setSections((prev) => [
      ...prev,
      { _id: newSectionId(), title: '', introHtml: '', order: prev.length + 1 },
    ]);
  };
  const removeSection = (index: number) => {
    setSections((prev) => {
      const next = prev.filter((_, i) => i !== index).map((s, i2) => ({ ...s, order: i2 + 1 }));
      // Clear section assignment on questions that pointed to removed section
      const removedId = prev[index]?._id;
      if (removedId) {
        setQuestions((qs) => qs.map((q) => (q.sectionId === removedId ? { ...q, sectionId: null } : q)));
      }
      return next;
    });
  };
  const moveSection = (index: number, dir: 'up' | 'down') => {
    setSections((prev) => {
      const target = dir === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };
  const updateSection = (index: number, patch: Partial<Section>) => {
    setSections((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch } as Section;
      return next;
    });
  };

  // After options load, map initial ids to objects
  useEffect(() => {
    if (allUsers.length && initialAssignedUserIds.length) {
      setAssignedUsersSel(allUsers.filter(u => initialAssignedUserIds.includes(u._id)));
    }
  }, [allUsers, initialAssignedUserIds]);

  useEffect(() => {
    if (allTeams.length && initialAssignedTeamIds.length) {
      setAssignedTeamsSel(allTeams.filter(t => initialAssignedTeamIds.includes(t._id)));
    }
  }, [allTeams, initialAssignedTeamIds]);

  // Infer boundaries from loaded questions' sectionId
  useEffect(() => {
    if (!questions.length) { setSectionBoundaries([]); return; }
    const inferred: { sectionId: string; index: number }[] = [];
    let last: string | null = null;
    questions.forEach((q, i) => {
      const cur = q.sectionId ? String(q.sectionId) : null;
      if (cur && cur !== last) {
        inferred.push({ sectionId: cur, index: i });
      }
      last = cur;
    });
    setSectionBoundaries(inferred);
  }, [questions]);

  const recomputeAssignmentsFromBoundaries = (boundaries: { sectionId: string; index: number }[]) => {
    const sorted = [...boundaries].sort((a, b) => a.index - b.index);
    setQuestions((prev) => {
      const next = prev.map((q) => ({ ...q }));
      let b = 0;
      let current: string | null = null;
      for (let i = 0; i < next.length; i++) {
        if (b < sorted.length && i === sorted[b].index) {
          current = sorted[b].sectionId;
          b++;
        }
        next[i].sectionId = current;
      }
      return next;
    });
    // Update sections order to follow boundaries order
    setSections((prev) => {
      const orderMap = new Map(sorted.map((s, i) => [s.sectionId, i + 1]));
      return prev.map((s) => ({ ...s, order: orderMap.get(String(s._id)) || s.order || 0 }));
    });
    setSectionBoundaries(sorted);
  };

  const insertSectionAt = (insertIndex: number) => {
    const title = window.prompt('Section title (optional)', '') || '';
    const newId = newSectionId();
    setSections((prev) => {
      const next = [...prev, { _id: newId, title, introHtml: '', order: prev.length + 1 }];
      return next;
    });
    const newBounds = [...sectionBoundaries, { sectionId: newId, index: Math.max(0, Math.min(insertIndex, questions.length)) }];
    recomputeAssignmentsFromBoundaries(newBounds);
  };

  // When removing a section, also drop its boundary and reassign
  const removeSectionAndBoundary = (index: number) => {
    const removedId = sections[index]?._id as string | undefined;
    removeSection(index);
    if (removedId) {
      const newBounds = sectionBoundaries.filter((b) => b.sectionId !== removedId);
      recomputeAssignmentsFromBoundaries(newBounds);
    }
  };

  const fetchSurvey = async () => {
    try {
      setLoading(true);
      const response = await surveyAPI.getSurvey(id!);
      const survey = response.data;
      
      setFormData({
        title: survey.title,
        description: survey.description || '',
        introHtml: survey.introHtml || '',
        startDate: survey.startDate ? new Date(survey.startDate).toISOString().split('T')[0] : '',
        endDate: survey.endDate ? new Date(survey.endDate).toISOString().split('T')[0] : '',
        targetDepartment: survey.targetDepartment || '',
        anonymousResponses: !!survey.anonymousResponses,
        isActive: !!survey.isActive,
        isTemplate: Boolean(survey.isTemplate),
      });
      
      // Normalize allowComment default if backend did not persist it
      setQuestions((survey.questions || []).map((q: any) => ({
        ...q,
        allowComment: typeof q.allowComment === 'boolean'
          ? q.allowComment
          : (q.questionType === 'rating' || q.questionType === 'multiple_choice'),
      })));
      setSections((survey.sections || []).sort((a: any, b: any) => (a.order || 0) - (b.order || 0)));
      // Preserve assignments if present
      setInitialAssignedUserIds((survey.assignedUsers || []).map((u:any) => typeof u === 'string' ? u : u._id));
      setInitialAssignedTeamIds((survey.assignedTeams || []).map((t:any) => typeof t === 'string' ? t : t._id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch survey');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: e.target.type === 'checkbox' ? checked : value,
    });
  };

  const openTemplateDialog = async () => {
    try {
      setTemplatesError('');
      setTemplatesLoading(true);
      setTemplateDialogOpen(true);
      const res = await surveyAPI.getTemplates();
      setTemplates(res.data || []);
    } catch (err: any) {
      setTemplatesError(err.response?.data?.message || 'Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleUseTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      setError('');
      // Use current scheduling and flags from the form
      await surveyAPI.createFromTemplate(templateId, {
        startDate: formData.startDate,
        endDate: formData.endDate,
        targetDepartment: formData.targetDepartment,
        anonymousResponses: formData.anonymousResponses,
      });
      setSuccess('Survey created from template!');
      setTemplateDialogOpen(false);
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create survey from template');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionChange = useCallback((index: number, field: keyof Question, value: any) => {
    setQuestions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value } as any;
      return next;
    });
  }, []);

  const duplicateQuestion = useCallback((index: number) => {
    const source = questions[index];
    const clone: Question = {
      ...source,
      // clear Mongo _id if present; ensure a new order placeholder
      // @ts-ignore
      _id: undefined,
      order: source.order + 1,
    } as any;

    const updated = [...questions];
    // insert clone after the source
    updated.splice(index + 1, 0, clone);
    // reassign order sequentially
    updated.forEach((q, i) => { q.order = i + 1; });
    setQuestions(updated);
  }, [questions]);

  const handleOptionChange = useCallback((questionIndex: number, optionIndex: number, value: string) => {
    const updatedQuestions = [...questions];
    const current = { ...(updatedQuestions[questionIndex] as any) } as any;
    const options = [...(current.options || [])];
    options[optionIndex] = value;
    current.options = options;
    updatedQuestions[questionIndex] = current;
    setQuestions(updatedQuestions);
  }, [questions]);

  const addOption = useCallback((questionIndex: number) => {
    const updatedQuestions = [...questions];
    const current = { ...(updatedQuestions[questionIndex] as any) } as any;
    current.options = [
      ...(current.options || []),
      '',
    ];
    // ensure points array stays aligned
    const pts = Array.isArray(current.points) ? [...(current.points as any)] : [];
    pts.push(0);
    current.points = pts;
    updatedQuestions[questionIndex] = current;
    setQuestions(updatedQuestions);
  }, [questions]);

  const removeOption = useCallback((questionIndex: number, optionIndex: number) => {
    const updatedQuestions = [...questions];
    const current = { ...(updatedQuestions[questionIndex] as any) } as any;
    current.options = current.options?.filter((_: any, i: number) => i !== optionIndex);
    // remove corresponding points entry
    if (Array.isArray(current.points)) {
      current.points = current.points.filter((_: any, i: number) => i !== optionIndex);
    }
    updatedQuestions[questionIndex] = current;
    setQuestions(updatedQuestions);
  }, [questions]);

  // Question list operations (used by inline UI and external card)
  const moveQuestion = useCallback((index: number, dir: 'up' | 'down') => {
    setQuestions((prev) => {
      const target = dir === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((q, i) => ({ ...q, order: i + 1 }));
    });
  }, []);

  const removeQuestion = useCallback((index: number) => {
    setQuestions((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== index).map((q, i2) => ({ ...q, order: i2 + 1 }));
      return next;
    });
  }, []);

  const addQuestion = useCallback(() => {
    setQuestions((prev) => ([
      ...prev,
      {
        questionText: '',
        questionType: 'text',
        options: [],
        ratingMinLabel: '',
        ratingMaxLabel: '',
        ratingScale: 5,
        ratingLabels: ['', '', '', '', ''],
        required: false,
        order: prev.length + 1,
      } as any,
    ]));
  }, []);

  // Memoized per-question editor to avoid re-rendering all questions on each keystroke  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    // no-op

    // Validation
    if (!formData.title.trim()) {
      try { console.error('[CreateSurvey] validation: missing title'); } catch (_) {}
      setError('Survey title is required');
      return;
    }

    if (questions.some((q) => !q.questionText.trim())) {
      try { console.error('[CreateSurvey] validation: one or more questions missing text'); } catch (_) {}
      setError('All questions must have text');
      return;
    }

    if (
      questions.some(
        (q) =>
          q.questionType === 'multiple_choice' &&
          (!q.options || q.options.length < 2 || q.options.some((o) => !o.trim()))
      )
    ) {
      try { console.error('[CreateSurvey] validation: multiple choice options invalid'); } catch (_) {}
      setError('Multiple choice questions must have at least 2 non-empty options');
      return;
    }

    // Managers must select at least one assignee: either team(s) or user(s)
    if (!isAdmin) {
      const hasTeams = assignedTeamsSel.length > 0;
      const hasUsers = assignedUsersSel.length > 0;
      if (!hasTeams && !hasUsers) {
        try { console.error('[CreateSurvey] validation: no assignees selected (team or users)'); } catch (_) {}
        setError('Please select at least one team or user to assign this survey to');
        return;
      }
    }

    setLoading(true);

    try {
      // Sanitize questions for backend (omit frontend-only fields like allowComment)
      const sanitizedQuestions = questions.map((q: any) => {
        const out: any = {
          questionText: q.questionText,
          questionType: q.questionType,
          options: q.options || [],
          category: q.category,
          ratingMinLabel: q.ratingMinLabel,
          ratingMaxLabel: q.ratingMaxLabel,
          ratingScale: q.ratingScale,
          ratingLabels: q.ratingLabels || [],
          points: q.points || [],
          required: !!q.required,
          order: q.order,
          sectionId: q.sectionId ?? null,
        };
        return out;
      });

      const surveyData: any = {
        ...formData,
        questions: sanitizedQuestions,
        sections: [],
        assignedUsers: assignedUsersSel.map((u) => u._id),
        assignedTeams: assignedTeamsSel.map((t) => t._id),
      };

      // Admin create: all creations are templates with visibility controls
      if (isAdmin && !isEditMode) {
        // Do not send dates/active for templates
        delete surveyData.startDate;
        delete surveyData.endDate;
        delete surveyData.isActive;
        surveyData.visibility = adminVisibility;
        if (adminVisibility === 'selected') {
          surveyData.visibleOrganizations = adminSelectedOrgs.map((o) => o._id);
        }
      }

      // If admin creating and dates omitted, ensure inactive and no dates sent
      if (isAdmin && !isEditMode && !surveyData.isTemplate) {
        const missingDates = !surveyData.startDate || !surveyData.endDate;
        if (missingDates) {
          surveyData.isActive = false;
          delete surveyData.startDate;
          delete surveyData.endDate;
        }
      }

      if (isEditMode) {
        await surveyAPI.updateSurvey(id!, surveyData);
        setSuccess('Survey updated successfully!');
      } else if (isBasicManager && isUsingTemplate) {
        // Basic managers using templates should use createFromTemplate
        await surveyAPI.createFromTemplate(templateId!, {
          startDate: formData.startDate,
          endDate: formData.endDate,
          targetDepartment: formData.targetDepartment,
          anonymousResponses: formData.anonymousResponses,
          assignedUsers: assignedUsersSel.map((u) => u._id),
          assignedTeams: assignedTeamsSel.map((t) => t._id),
        });
        setSuccess('Survey created successfully!');
      } else {
        await surveyAPI.createSurvey(surveyData);
        setSuccess(isAdmin ? 'Template created successfully!' : 'Survey created successfully!');
      }

      if (!isEditMode) {
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} survey`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {isEditMode && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>
              Back to Surveys
            </Button>
          </Box>
        )}
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Survey' : 'Create New Survey'}
        </Typography>

        {error && !(isBasicManager && isUsingTemplate && error.includes('Basic manager accounts cannot create surveys')) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box
          component="form"
          noValidate
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // Prevent Enter from submitting the form and causing a page jump
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onSubmit={handleSubmit}
        >
          <Grid container spacing={3}>
            {!(isBasicManager && isEditMode) && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Survey Title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    minRows={2}
                    placeholder="Short summary for managers/admins (kept separate from the participant intro)"
                  />
                </Grid>

                {!isAdmin && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        name="startDate"
                        type="date"
                        value={formData.startDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        required={!formData.isTemplate}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        name="endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        required={!formData.isTemplate}
                      />
                    </Grid>
                  </>
                )}

                {/* Intro section editor */}
                <Grid item xs={12}>
                  <RichTextEditor
                    label="Intro (optional)"
                    value={formData.introHtml}
                    onChange={(html) => setFormData((prev) => ({ ...prev, introHtml: html }))}
                    minHeight={140}
                    placeholder="Write an introduction your employees will see before starting the survey..."
                  />
                </Grid>

                {/* Admin-only: template visibility controls */}
                {isAdmin && !isEditMode && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label="Template Visibility"
                        value={adminVisibility}
                        onChange={(e) => setAdminVisibility(e.target.value as any)}
                        SelectProps={{ native: true }}
                      >
                        <option value="global">Available to all organizations</option>
                        <option value="selected">Only selected organizations</option>
                      </TextField>
                    </Grid>
                    {adminVisibility === 'selected' && (
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          options={allOrgs}
                          value={adminSelectedOrgs}
                          onChange={(_: any, val: any) => setAdminSelectedOrgs(val)}
                          getOptionLabel={(o: any) => o.name}
                          renderInput={(params: any) => <TextField {...params} label="Select organizations" placeholder="Organizations" />}
                        />
                      </Grid>
                    )}
                  </>
                )}

                {/* Assignments (Users/Teams) */}
                {!isAdmin && !(isBasicManager && isEditMode) && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        multiple
                        options={allUsers as any[]}
                        value={assignedUsersSel as any[]}
                        onChange={(_: any, val: any[]) => setAssignedUsersSel(val)}
                        getOptionLabel={(o: any) => o.name + (o.email ? ` (${o.email})`  : '')}
                        renderInput={(params: any) => <TextField {...params} label="Assign to specific users (optional)" placeholder="Select users" />}
                        renderTags={(value: any[], getTagProps: (args: { index: number }) => any) =>
                          value.map((option: any, index: number) => {
                            const { key, ...chipProps } = getTagProps({ index });
                            return (
                              <Chip key={key} variant="outlined" label={option.name} {...chipProps} />
                            );
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        multiple
                        options={allTeams as any[]}
                        value={assignedTeamsSel as any[]}
                        onChange={(_: any, val: any[]) => setAssignedTeamsSel(val)}
                        getOptionLabel={(o: any) => o.name}
                        renderInput={(params: any) => <TextField {...params} label="Assign to teams (optional)" placeholder="Select teams" />}
                        renderTags={(value: any[], getTagProps: (args: { index: number }) => any) =>
                          value.map((option: any, index: number) => {
                            const { key, ...chipProps } = getTagProps({ index });
                            return (
                              <Chip key={key} variant="outlined" label={option.name} {...chipProps} />
                            );
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">You can combine team and individual assignments.</Typography>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.anonymousResponses}
                        onChange={handleChange}
                        name="anonymousResponses"
                      />
                    }
                    label="Anonymous Responses"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={handleChange}
                        name="isActive"
                      />
                    }
                    label="Active"
                  />
                </Grid>

                {/* Admin template toggle removed: all admin creations are templates */}
              </>
            )}
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Sections UI disabled as sections are now represented by display-only questions */}

          <Typography variant="h5" gutterBottom>
            Questions
          </Typography>

          {(() => {
            const start = page * pageSize;
            const end = Math.min(start + pageSize, questions.length);
            return questions.slice(start, end).map((question, idx) => {
              const qIndex = start + idx;
              return (
                <SurveyQuestionCard
                  key={String((question as any)?._id || `idx-${qIndex}`)}
                  question={question as any}
                  qIndex={qIndex}
                  questionsLength={questions.length}
                  moveQuestion={moveQuestion}
                  duplicateQuestion={duplicateQuestion}
                  removeQuestion={removeQuestion}
                  handleQuestionChange={handleQuestionChange}
                  setQuestions={setQuestions}
                  handleOptionChange={handleOptionChange}
                  addOption={addOption}
                  removeOption={removeOption}
                  readOnly={isReadOnlyQuestions}
                />
              );
            });
          })()}

          {!isReadOnlyQuestions && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addQuestion}
              fullWidth
              sx={{ mb: 3 }}
            >
              Add Question
            </Button>
          )}

          {/* Pagination controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2">Showing {Math.min(page * pageSize + 1, questions.length)}–{Math.min((page + 1) * pageSize, questions.length)} of {questions.length}</Typography>
            <Box>
              <Button size="small" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} sx={{ mr: 1 }}>
                Prev
              </Button>
              <Button size="small" onClick={() => setPage((p) => (p + 1 < Math.ceil(questions.length / pageSize) ? p + 1 : p))} disabled={page + 1 >= Math.ceil(questions.length / pageSize)}>
                Next
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => navigate('/dashboard')}>
              Cancel
            </Button>
            {isAdmin && !isEditMode && (
              <Button variant="outlined" onClick={openTemplateDialog} disabled={templatesLoading}>
                {templatesLoading ? <CircularProgress size={20} /> : 'Start from Template'}
              </Button>
            )}
            {(!isReadOnlyQuestions || (isBasicManager && isUsingTemplate)) && (
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : isEditMode ? 'Update Survey' : 'Create Survey'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Templates Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select a Template</DialogTitle>
        <DialogContent>
          {templatesError && (
            <Alert severity="error" sx={{ mb: 2 }}>{templatesError}</Alert>
          )}
          {templatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <MUIList>
              {templates.length === 0 && (
                <Typography variant="body2" color="text.secondary">No templates available</Typography>
              )}
              {templates.map((tpl) => (
                <ListItem key={tpl._id} divider secondaryAction={
                  <Button size="small" variant="contained" onClick={() => handleUseTemplate(tpl._id)}>
                    Use
                  </Button>
                }>
                  <ListItemText
                    primary={tpl.title}
                    secondary={tpl.description || 'No description'}
                  />
                </ListItem>
              ))}
            </MUIList>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreateSurvey;
